import { Controller } from "@hotwired/stimulus";
import {
  pushOverlay,
  popOverlay,
  topmostOverlay,
} from "./overlay_stack";

// Modal genérico — patrón DHH turbo-frame global.
//
// Dos modos de uso:
//   1. Turbo Frame global: el modal se carga vía <turbo-frame id="modal"> en el layout.
//      El controller se conecta con autoOpen=true y se abre automáticamente.
//      Al cerrar, vacía el frame global.
//
//   2. Inline (legacy): el modal vive dentro de la página con un botón open.
//      Se abre vía data-action="manage--modal#open".
//      Al cerrar, restaura el contenido original.
//
// Cierre automático en submit exitoso vía turbo:submit-end.
// Error 422: turbo-frame "modal_form" reemplaza solo el form (sin re-animación).

export default class extends Controller {
  static targets = ["overlay", "panel"];
  static values = {
    autoOpen: { type: Boolean, default: false },
    autoClose: { type: Boolean, default: true },
    closeOnBackdrop: { type: Boolean, default: true },
  };

  connect() {
    this._isGlobal = !!this.element.closest("turbo-frame#modal");
    this._closing = false;

    if (!this._isGlobal) {
      this._originalChildren = Array.from(this.panelTarget.children).map(
        (child) => child.cloneNode(true)
      );
    }

    this._escHandler = (e) => {
      if (e.key === "Escape") this._closeIfTopmost();
    };
    document.addEventListener("keydown", this._escHandler);

    this._mouseDownTarget = null;
    this._mouseDownHandler = (e) => {
      this._mouseDownTarget = e.target;
    };
    this.overlayTarget.addEventListener("mousedown", this._mouseDownHandler);

    // Cierre automático cuando un form dentro del modal se envía exitosamente.
    this._submitEndHandler = (e) => {
      if (this.autoCloseValue && e.detail.success) this.close();
    };
    this.element.addEventListener("turbo:submit-end", this._submitEndHandler);

    if (this.autoOpenValue) this.open();
  }

  open() {
    this._closing = false;
    this._cleanupDone = false;
    pushOverlay(this.overlayTarget);
    this.overlayTarget.classList.remove("hidden");
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => {
      this.panelTarget.classList.add("modal-panel--open");
    });
  }

  close() {
    if (this._closing) return;
    if (!this.panelTarget.classList.contains("modal-panel--open")) return;
    this._closing = true;

    this.panelTarget.classList.remove("modal-panel--open");
    document.body.style.overflow = "";

    const cleanup = () => {
      if (this._cleanupDone) return;
      this._cleanupDone = true;
      clearTimeout(this._closeTimeout);

      this.overlayTarget.classList.add("hidden");
      popOverlay(this.overlayTarget);

      if (this._isGlobal) {
        const frame = this.element.closest("turbo-frame#modal");
        if (frame) frame.innerHTML = "";
      } else {
        this._restoreOriginalContent();
      }
    };

    // Usar transitionend para timing preciso
    this.panelTarget.addEventListener("transitionend", cleanup, { once: true });
    // Fallback por si transitionend no dispara
    this._closeTimeout = setTimeout(cleanup, 300);
  }

  closeOnOverlay(event) {
    if (!this.closeOnBackdropValue) {
      this._mouseDownTarget = null;
      return;
    }
    if (
      event.target === this.overlayTarget &&
      this._mouseDownTarget === this.overlayTarget
    ) {
      this.close();
    }
    this._mouseDownTarget = null;
  }

  disconnect() {
    clearTimeout(this._closeTimeout);
    document.removeEventListener("keydown", this._escHandler);
    if (this.hasOverlayTarget) {
      this.overlayTarget.removeEventListener("mousedown", this._mouseDownHandler);
    }
    this.element.removeEventListener("turbo:submit-end", this._submitEndHandler);
    popOverlay(this.overlayTarget);
    document.body.style.overflow = "";
  }

  // --- Private ---

  _closeIfTopmost() {
    if (!this.panelTarget.classList.contains("modal-panel--open")) return;
    if (topmostOverlay() === this.overlayTarget) {
      this.close();
    }
  }

  _restoreOriginalContent() {
    if (!this._originalChildren) return;
    this.panelTarget.replaceChildren();
    this._originalChildren.forEach((child) => {
      this.panelTarget.appendChild(child.cloneNode(true));
    });
  }
}
